import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { useUnsavedGuard } from "@/hooks/useUnsavedGuard";
import { toast } from "sonner";

const STATUS = [
  { value: "ativo", label: "Ativo" },
  { value: "planejado", label: "Planejado" },
  { value: "encerrado", label: "Encerrado" },
];
const BANCOS = [
  { value: "negociacao", label: "Negociação Policial" },
  { value: "primeiro_interventor", label: "Primeiro Interventor" },
  { value: "manual", label: "Nenhum / montar manualmente" },
];
const empty = () => ({ nome: "", descricao: "", finalidade: "", periodo: "", carga_horaria: "", status: "ativo", usa_tipologia_cec: false, permite_aplicacoes_instrutor: false, tipo: "curso", banco_criterios: "manual" });

export function CursoFormDialog({ open, onOpenChange, curso, onSaved, defaultTipo = "curso" }) {
  const [form, setForm] = useState(empty());
  const { setBaseline, guard } = useUnsavedGuard();

  useEffect(() => {
    if (!open) return;
    const f = curso
      ? { nome: curso.nome || "", descricao: curso.descricao || "", finalidade: curso.finalidade || "", periodo: curso.periodo || "", carga_horaria: curso.carga_horaria || "", status: curso.status || "ativo", usa_tipologia_cec: !!curso.usa_tipologia_cec, permite_aplicacoes_instrutor: !!curso.permite_aplicacoes_instrutor, tipo: curso.tipo || "curso", banco_criterios: curso.banco_criterios || "manual" }
      : { ...empty(), tipo: defaultTipo, banco_criterios: defaultTipo === "instrucao" ? "primeiro_interventor" : "negociacao", permite_aplicacoes_instrutor: defaultTipo === "instrucao" };
    setForm(f); setBaseline(f);
  }, [open, curso, defaultTipo]);

  const isInstrucao = form.tipo === "instrucao";

  const save = async () => {
    try {
      if (curso) { await api.put(`/cursos/${curso.id}`, form); toast.success(isInstrucao ? "Instrução atualizada" : "Curso atualizado"); onOpenChange(false); onSaved && onSaved(); }
      else { const { data } = await api.post("/cursos", form); toast.success(isInstrucao ? "Instrução criada" : "Curso criado"); onOpenChange(false); onSaved && onSaved(data); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={guard(onOpenChange, form)}>
      <DialogContent className="bg-popover border-border">
        <DialogHeader><DialogTitle>{curso ? (isInstrucao ? "Editar instrução" : "Editar curso") : (isInstrucao ? "Nova instrução" : "Novo curso")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input data-testid="curso-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
          <div><Label>Descrição</Label><Textarea data-testid="curso-descricao" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} /></div>
          <div><Label>Finalidade</Label><Textarea data-testid="curso-finalidade" value={form.finalidade} onChange={(e) => setForm({ ...form, finalidade: e.target.value })} placeholder="Para que serve esta formação" /></div>
          <div>
            <Label>Banco de critérios base</Label>
            <Select value={form.banco_criterios} onValueChange={(v) => setForm({ ...form, banco_criterios: v })}>
              <SelectTrigger data-testid="curso-banco"><SelectValue placeholder="Selecione o banco" /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {BANCOS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">Fonte dos critérios ao criar oficinas desta formação. Você escolhe quais critérios usar (não copia todos automaticamente).</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Período</Label><Input data-testid="curso-periodo" value={form.periodo} onChange={(e) => setForm({ ...form, periodo: e.target.value })} /></div>
            <div><Label>Carga horária</Label><Input data-testid="curso-carga" value={form.carga_horaria} onChange={(e) => setForm({ ...form, carga_horaria: e.target.value })} /></div>
          </div>
          <div>
            <Label>Situação</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="curso-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover border-border">
                {STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between rounded-md border border-border p-3">
            <div className="pr-3">
              <Label>Usa Tipologia do CEC</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Torna a tipologia obrigatória nas oficinas (ex.: Negociação Policial).</p>
            </div>
            <Switch data-testid="curso-usa-tipologia" checked={form.usa_tipologia_cec} onCheckedChange={(v) => setForm({ ...form, usa_tipologia_cec: v })} />
          </div>
        </div>
        <DialogFooter><Button data-testid="salvar-curso-btn" onClick={save} disabled={!form.nome}>{curso ? "Salvar alterações" : "Salvar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
